// Global Instructions Rule Applied!
//
// Meeting watches (Teams transcript ingestion) — admin HTTP service for
// Configuration → Interaction Tracking → Meeting watches.

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  MeetingWatchRow,
  validateMeetingWatchList
} from '../../../state_manager/data_models/meeting_watch_model.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const jsonHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

const readBody = async (response: Response, failureLabel: string): Promise<unknown> => {
  const body = await readJsonBody<{ data?: unknown; error?: string; message?: string }>(response)
  if (!response.ok) {
    throw new Error(
      body?.message || body?.error || `${failureLabel}: ${response.status} ${response.statusText}`
    )
  }
  return body?.data
}

export const fetchMeetingWatches = async (
  accessToken: string | null
): Promise<MeetingWatchRow[]> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.MEETING_TRANSCRIPTS.WATCHES)
  const response = await fetch(url, { method: 'GET', headers: jsonHeaders(accessToken) })
  return validateMeetingWatchList(await readBody(response, 'Meeting watches read failed'))
}

export interface MeetingWatchSavePayload {
  id?: string | null
  label: string
  organizer_email: string
  /** The organizer's Entra object id — what the Graph onlineMeetings URLs
   *  require (a UPN 400s there). Optional: empty falls back to resolving the
   *  email, which needs User.Read.All on the reader app. */
  organizer_user_id?: string | null
  join_web_url: string
  subject_match?: string | null
  enabled?: boolean
  notes?: string | null
}

export const saveMeetingWatch = async (
  accessToken: string | null,
  payload: MeetingWatchSavePayload
): Promise<unknown> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.MEETING_TRANSCRIPTS.WATCH_SAVE)
  const response = await fetch(url, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: payload })
  })
  return readBody(response, 'Meeting watch save failed')
}

export const deleteMeetingWatch = async (
  accessToken: string | null,
  id: string
): Promise<unknown> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.MEETING_TRANSCRIPTS.WATCH_DELETE)
  const response = await fetch(url, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: { id } })
  })
  return readBody(response, 'Meeting watch delete failed')
}

export const resolveMeetingWatch = async (
  accessToken: string | null,
  id: string
): Promise<unknown> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.MEETING_TRANSCRIPTS.WATCH_RESOLVE)
  const response = await fetch(url, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: { id } })
  })
  return readBody(response, 'Meeting watch resolve failed')
}
